/**
 * Helper function to handle component-level saves
 * @param {Object} lead - Current lead object
 * @param {Object} changedData - Only the changed fields
 * @param {Function} updateLead - API function to update lead
 * @param {Function} showToast - Toast notification function
 * @param {Function} setCurrentLead - State setter for current lead
 * @returns {Promise<boolean>} - Whether save was successful
 */
export const handleComponentSave = async (
  lead,
  changedData,
  addSchedule,
  showToast,
  setCurrentLead
) => {
  if (!lead?._id) return false;

  try {
    const payload = new FormData();
    payload.append("clientId", lead._id);
    // Add only changed fields to FormData
    Object.entries(changedData).forEach(([key, value]) => {
      if (key === "profileImage" && value && value instanceof File) {
        payload.append("profileImage", value);
      } else if (Array.isArray(value) || (value && typeof value === "object" && !(value instanceof File))) {
        payload.append(key, JSON.stringify(value));
      } else {
        payload.append(key, value || "");
      }
    });

    const response = await addSchedule(lead._id, payload);

    if (response) {
      const updatedLead = response?.data || response;
      setCurrentLead(updatedLead);
      showToast("Changes saved successfully", "success");
      return { success: true };
    }
  } catch (error) {
    console.error("Error saving changes:", error);
    const responseData = error?.response?.data;
    showToast(responseData?.message || "Failed to save changes", "error");

    // Try to parse validation errors into a simple map: { fieldName: message }
    const mapped = {};
    if (Array.isArray(responseData?.errors) && responseData.errors.length) {
      responseData.errors.forEach((err) => {
        const path = err.path || err.param || err.field || "";
        if (!path) return;
        if (!mapped[path]) mapped[path] = err.msg || String(err.message || err.msg || "Invalid value");
        else mapped[path] = `${mapped[path]} | ${err.msg || err.message}`;
      });
    }

    return { success: false, errors: Object.keys(mapped).length ? mapped : undefined };
  }

  return { success: false };
};
